import * as React from "react";

import { Button } from "@repo/ui/components/button";
import { Checkbox } from "@repo/ui/components/checkbox";
import { Input } from "@repo/ui/components/input";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@repo/ui/components/sheet";
import { Settings2, XIcon } from "lucide-react";

import { useMediaDayStatusLanguage } from "./status-language";

type ScheduleSettings = {
  teamGapMinutes: number;
  picturesAvailableDelayMinutes: number;
  editDeadlineDelayMinutes: number;
};

type ActiveSet = {
  key: string;
  label: string;
  participantMode: "individual" | "team";
};

const padTime = (value: number) => value.toString().padStart(2, "0");
const minutesToTime = (minutes: number | null) => {
  if (minutes === null) return "";
  const safeMinutes = ((minutes % 1440) + 1440) % 1440;
  return `${padTime(Math.floor(safeMinutes / 60))}:${padTime(safeMinutes % 60)}`;
};
const timeToMinutes = (value: string) => {
  if (!value) return null;
  const [hours, minutes] = value.split(":").map(Number);
  return Number.isFinite(hours) && Number.isFinite(minutes) ? hours * 60 + minutes : null;
};
const readMinutes = (value: string, fallback: number) => {
  const minutes = Number(value);
  return Number.isFinite(minutes) ? Math.max(0, Math.min(1440, Math.round(minutes))) : fallback;
};

export function MediaDayAdminSettingsSheet({
  activeSet,
  settings,
  firstEtaMinutes,
  hasScheduleItems,
  playerCount,
  editorial,
  showAllRoles,
  onShowAllRolesChange,
  onSettingsChange,
  onApplySchedule,
}: {
  activeSet: ActiveSet | null;
  settings: ScheduleSettings;
  firstEtaMinutes: number | null;
  hasScheduleItems: boolean;
  playerCount: number;
  editorial: boolean;
  showAllRoles: boolean;
  onShowAllRolesChange: (showAllRoles: boolean) => void;
  onSettingsChange: (settings: Partial<ScheduleSettings>) => void;
  onApplySchedule: (etaMinutes: number | null, gapMinutes: number) => void;
}) {
  const { t } = useMediaDayStatusLanguage();
  return (
    <Sheet>
      <SheetTrigger
        render={
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="bg-white text-neutral-950 hover:bg-neutral-100"
          />
        }
      >
        <Settings2 className="size-4" /> {t("Admin settings")}
      </SheetTrigger>
      <SheetContent
        showCloseButton={false}
        className="bg-white text-neutral-950 data-[side=right]:w-full data-[side=right]:sm:max-w-xl"
      >
        <SheetHeader className="border-b border-neutral-200 pr-12">
          <SheetTitle className="text-neutral-950">{t("Media Day admin settings")}</SheetTitle>
          <SheetClose
            aria-label={t("Close")}
            render={
              <Button
                type="button"
                size="icon-sm"
                variant="ghost"
                className="absolute top-3 right-3 text-neutral-700 hover:bg-neutral-100"
              />
            }
          >
            <XIcon className="size-4" />
          </SheetClose>
          <SheetDescription className="text-neutral-500">
            {t("Configure the active set. Changes are saved automatically.")}
          </SheetDescription>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6">
          {activeSet ? (
            <div className="divide-y">
              <section className="pb-5">
                <div className="text-xs font-semibold tracking-wide text-neutral-500 uppercase">
                  {t("Active set")}
                </div>
                <div className="mt-1 text-base font-semibold">{activeSet.label}</div>
                <p className="mt-1 text-sm text-neutral-500">
                  {t("Workflow")}:{" "}
                  {t(activeSet.participantMode === "individual" ? "Individual" : "Team")}
                </p>
              </section>

              <section className="py-5">
                <div className="mb-4">
                  <h3 className="font-semibold">{t("Schedule")}</h3>
                  <p className="text-sm text-neutral-500">
                    {t("Set the start time and timing defaults for this set.")}
                  </p>
                </div>
                {hasScheduleItems ? (
                  <ScheduleFields
                    key={`${activeSet.key}:${firstEtaMinutes ?? "unset"}:${settings.teamGapMinutes}:${settings.picturesAvailableDelayMinutes}:${settings.editDeadlineDelayMinutes}`}
                    participantMode={activeSet.participantMode}
                    firstEtaMinutes={firstEtaMinutes}
                    playerCount={playerCount}
                    settings={settings}
                    onSettingsChange={onSettingsChange}
                    onApplySchedule={onApplySchedule}
                  />
                ) : (
                  <p className="rounded-md border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-500">
                    {t("This set has no schedule items to configure.")}
                  </p>
                )}
              </section>

              {editorial ? (
                <section className="py-5">
                  <h3 className="font-semibold">{t("Editorial visibility")}</h3>
                  <label className="mt-3 flex cursor-pointer items-start gap-3">
                    <Checkbox
                      checked={showAllRoles}
                      aria-label={t("Show all editorial roles")}
                      className="border-neutral-400 bg-white text-neutral-950"
                      onCheckedChange={(checked) => onShowAllRolesChange(checked === true)}
                    />
                    <span>
                      <span className="block text-sm font-medium">{t("Show all roles")}</span>
                      <span className="block text-sm text-neutral-500">
                        {t("Include staff and other normally hidden editorial participants.")}
                      </span>
                    </span>
                  </label>
                </section>
              ) : null}
            </div>
          ) : (
            <p className="rounded-md border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-500">
              {t("Select a set to configure its administrative settings.")}
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ScheduleFields({
  participantMode,
  firstEtaMinutes,
  playerCount,
  settings,
  onSettingsChange,
  onApplySchedule,
}: {
  participantMode: ActiveSet["participantMode"];
  firstEtaMinutes: number | null;
  playerCount: number;
  settings: ScheduleSettings;
  onSettingsChange: (settings: Partial<ScheduleSettings>) => void;
  onApplySchedule: (etaMinutes: number | null, gapMinutes: number) => void;
}) {
  const { language, plural, t } = useMediaDayStatusLanguage();
  const [startTime, setStartTime] = React.useState(minutesToTime(firstEtaMinutes));
  const [gap, setGap] = React.useState(String(settings.teamGapMinutes));
  const [picturesDelay, setPicturesDelay] = React.useState(
    String(settings.picturesAvailableDelayMinutes),
  );
  const [deadlineDelay, setDeadlineDelay] = React.useState(
    String(settings.editDeadlineDelayMinutes),
  );
  const individual = participantMode === "individual";
  const applyStart = (value: string, gapMinutes = settings.teamGapMinutes) =>
    onApplySchedule(timeToMinutes(value), gapMinutes);
  const applyGap = () => {
    const value = readMinutes(gap, settings.teamGapMinutes);
    setGap(String(value));
    onSettingsChange({ teamGapMinutes: value });
    if (startTime) applyStart(startTime, value);
  };
  const applyPicturesDelay = () => {
    const value = readMinutes(picturesDelay, settings.picturesAvailableDelayMinutes);
    setPicturesDelay(String(value));
    onSettingsChange({ picturesAvailableDelayMinutes: value });
  };
  const applyDeadlineDelay = () => {
    const value = readMinutes(deadlineDelay, settings.editDeadlineDelayMinutes);
    setDeadlineDelay(String(value));
    onSettingsChange({ editDeadlineDelayMinutes: value });
  };

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <label className="grid gap-1.5 text-sm font-medium">
        {t("First")} {t(individual ? "player" : "team")}
        <Input
          type="time"
          aria-label={`${t("First")} ${t(individual ? "player" : "team")}`}
          value={startTime}
          className="border-neutral-300 bg-white font-semibold text-neutral-950 tabular-nums"
          onBlur={() => applyStart(startTime)}
          onChange={(event) => setStartTime(event.currentTarget.value)}
        />
      </label>
      <MinutesInput
        label={`${t("Between")} ${individual ? t("players") : language === "pl" ? "teamami" : "teams"}`}
        value={gap}
        onChange={setGap}
        onBlur={applyGap}
      />
      <MinutesInput
        label={t("Time for shoot and selection")}
        value={picturesDelay}
        onChange={setPicturesDelay}
        onBlur={applyPicturesDelay}
      />
      <MinutesInput
        label={t("Editing time for one player")}
        value={deadlineDelay}
        onChange={setDeadlineDelay}
        onBlur={applyDeadlineDelay}
      />
      <p className="text-xs text-neutral-500 sm:col-span-2">
        {t("The edit deadline uses")} {deadlineDelay || 0} {t("min")} × {playerCount}{" "}
        {plural(
          playerCount,
          language === "pl"
            ? ["uprawnionego gracza", "uprawnionych graczy", "uprawnionych graczy"]
            : ["eligible player", "eligible players", "eligible players"],
        )}
        .
      </p>
    </div>
  );
}

function MinutesInput({
  label,
  value,
  onChange,
  onBlur,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
}) {
  const { t } = useMediaDayStatusLanguage();
  return (
    <label className="grid gap-1.5 text-sm font-medium">
      {label}
      <div className="flex items-center gap-2">
        <Input
          value={value}
          aria-label={label}
          inputMode="numeric"
          className="border-neutral-300 bg-white font-semibold text-neutral-950 tabular-nums"
          onBlur={onBlur}
          onChange={(event) => onChange(event.currentTarget.value)}
        />
        <span className="text-xs text-neutral-500">{t("min")}</span>
      </div>
    </label>
  );
}
